import { useParams, Link, Navigate } from "react-router-dom";
import { useState } from "react";
import {
  CheckCircle,
  Warning,
  CaretDown,
  ArrowRight,
} from "@phosphor-icons/react";
import SEO from "../components/SEO";
import CallButton from "../components/CallButton";
import TrustBar from "../components/TrustBar";
import FinalCTABanner from "../components/FinalCTABanner";
import { SERVICES, getService } from "../data/services";
import { CITIES } from "../data/cities";
import { BUSINESS, IMAGES } from "../data/constants";

function Faq({ faqs }) {
  const [open, setOpen] = useState(0);
  return (
    <div className="mt-8 divide-y divide-[#E2E8F0] border-t border-b border-[#E2E8F0]" data-testid="faq-list">
      {faqs.map((f, i) => (
        <div key={i}>
          <button
            data-testid={`faq-toggle-${i}`}
            onClick={() => setOpen(open === i ? -1 : i)}
            className="w-full flex items-center justify-between gap-4 py-5 text-left"
          >
            <h3 className="font-display font-bold text-lg md:text-xl text-[#1B2A4A] tracking-tight">
              {f.q}
            </h3>
            <CaretDown
              size={20}
              weight="bold"
              className={`shrink-0 text-[#FF6B00] transition-transform ${open === i ? "rotate-180" : ""}`}
            />
          </button>
          {open === i && (
            <p
              data-testid={`faq-answer-${i}`}
              className="pb-5 text-[#4B5563] leading-relaxed"
            >
              {f.a}
            </p>
          )}
        </div>
      ))}
    </div>
  );
}

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = getService(slug);

  if (!service) return <Navigate to="/404" replace />;

  const related = SERVICES.filter((s) => s.slug !== slug).slice(0, 3);
  const featuredCities = CITIES.slice(0, 3);

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        serviceType: service.name,
        provider: {
          "@type": "LocalBusiness",
          name: BUSINESS.name,
          telephone: BUSINESS.phone,
          areaServed: "Greater Toronto Area",
        },
        areaServed: CITIES.map((c) => ({ "@type": "City", name: c.name })),
        description: service.metaDescription,
      },
      {
        "@type": "FAQPage",
        mainEntity: service.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: BUSINESS.url },
          { "@type": "ListItem", position: 2, name: "Services", item: `${BUSINESS.url}/services` },
          {
            "@type": "ListItem",
            position: 3,
            name: service.name,
            item: `${BUSINESS.url}/services/${service.slug}`,
          },
        ],
      },
    ],
  };

  return (
    <>
      <SEO
        title={service.metaTitle}
        description={service.metaDescription}
        canonical={`/services/${service.slug}`}
        ogImage={service.image}
        schema={schema}
      />

      {/* Hero */}
      <section className="relative bg-[#1B2A4A] text-white" data-testid="service-detail-hero">
        <img
          src={service.image}
          alt={`${service.name} performed by a GTA Garage Doors technician`}
          className="absolute inset-0 w-full h-full object-cover opacity-30"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1B2A4A] via-[#1B2A4A]/85 to-[#1B2A4A]/40" aria-hidden />

        <div className="relative mx-auto max-w-7xl px-4 md:px-6 py-16 md:py-24">
          <nav className="text-xs text-white/60 font-bold uppercase tracking-widest font-display" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-[#FF6B00]">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/services" className="hover:text-[#FF6B00]">Services</Link>
            <span className="mx-2">/</span>
            <span className="text-white">{service.name}</span>
          </nav>
          <h1 className="mt-5 font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-3xl leading-[1.05]">
            {service.name} <span className="text-[#FF6B00]">in Toronto & GTA</span>
          </h1>
          <div className="mt-8">
            <CallButton size="lg" testId="service-hero-call-button" />
          </div>
        </div>
      </section>

      <TrustBar variant="dark" />

      <div className="section-pad bg-white">
        <div className="container-tight grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
          {/* Main content */}
          <div className="lg:col-span-8">
            <p className="text-base md:text-lg text-[#1B2A4A] leading-relaxed" data-testid="service-intro">
              {service.intro}
            </p>

            {/* What We Fix */}
            <div className="mt-12">
              <div className="eyebrow">What we fix / what we do</div>
              <h2 className="mt-3 font-display font-black text-2xl md:text-3xl tracking-tight text-[#1B2A4A]">
                The exact problems we solve.
              </h2>
              <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="what-we-fix-list">
                {service.whatWeFix.map((item, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 text-[#1B2A4A]"
                  >
                    <CheckCircle size={22} weight="fill" className="text-[#FF6B00] shrink-0 mt-0.5" />
                    <span className="leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Urgency Triggers */}
            <div
              data-testid="urgency-triggers"
              className="mt-12 rounded-sm border-l-4 border-[#FF6B00] bg-[#FFF7F0] p-6 md:p-8"
            >
              <div className="flex items-center gap-2 text-[#FF6B00]">
                <Warning size={22} weight="fill" />
                <span className="font-display font-black uppercase tracking-widest text-sm">Signs You Need This Service Now</span>
              </div>
              <ul className="mt-4 space-y-3">
                {service.urgencyTriggers.map((t, i) => (
                  <li key={i} className="flex items-start gap-3 text-[#1B2A4A]">
                    <span className="mt-2 h-2 w-2 rounded-full bg-[#FF6B00] shrink-0" aria-hidden />
                    <span className="leading-relaxed font-medium">{t}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-6">
                <CallButton testId="urgency-call-button" />
              </div>
            </div>

            {/* FAQ */}
            <div className="mt-12">
              <div className="eyebrow">Frequently asked questions</div>
              <h2 className="mt-3 font-display font-black text-2xl md:text-3xl tracking-tight text-[#1B2A4A]">
                {service.name} FAQ
              </h2>
              <Faq faqs={service.faqs} />
            </div>
          </div>

          {/* Sticky sidebar */}
          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-28 space-y-4" data-testid="service-sidebar">
              <div className="rounded-sm border border-[#E2E8F0] p-6 bg-white">
                <div className="eyebrow">Dispatch 24/7</div>
                <div className="mt-2 font-display font-black text-2xl text-[#1B2A4A]">
                  Speak to a real tech in under 60 seconds.
                </div>
                <div className="mt-5">
                  <CallButton size="lg" testId="sidebar-call-button" block />
                </div>
                <ul className="mt-5 space-y-2 text-sm text-[#1B2A4A]">
                  <li className="flex items-center gap-2"><CheckCircle size={18} weight="fill" className="text-[#FF6B00]"/>Licensed & insured</li>
                  <li className="flex items-center gap-2"><CheckCircle size={18} weight="fill" className="text-[#FF6B00]"/>60-minute GTA response</li>
                  <li className="flex items-center gap-2"><CheckCircle size={18} weight="fill" className="text-[#FF6B00]"/>Same-day repair</li>
                </ul>
              </div>

              <div className="rounded-sm border border-[#E2E8F0] p-6 bg-[#F8FAFC]">
                <div className="eyebrow">Also serving</div>
                <ul className="mt-3 space-y-1.5 text-sm">
                  {featuredCities.map((c) => (
                    <li key={c.slug}>
                      <Link
                        to={`/areas/${c.slug}`}
                        data-testid={`service-sidebar-city-${c.slug}`}
                        className="text-[#1B2A4A] hover:text-[#FF6B00] font-bold font-display inline-flex items-center gap-1"
                      >
                        {service.name} in {c.name}
                        <ArrowRight size={14} weight="bold"/>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <FinalCTABanner />

      {/* Related */}
      <section className="section-pad bg-[#F8FAFC]" data-testid="related-services">
        <div className="container-wide">
          <div className="eyebrow">Related services</div>
          <h2 className="mt-3 font-display font-black text-2xl md:text-3xl tracking-tight text-[#1B2A4A]">
            You might also need
          </h2>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {related.map((s) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                data-testid={`related-service-${s.slug}`}
                className="group block rounded-sm border border-[#E2E8F0] bg-white p-6 hover:-translate-y-1 hover:border-[#FF6B00] hover:shadow-md transition-all"
              >
                <div className="font-display font-black text-xl text-[#1B2A4A] tracking-tight">
                  {s.name}
                </div>
                <p className="mt-2 text-sm text-[#4B5563] leading-relaxed">{s.shortDesc}</p>
                <div className="mt-4 inline-flex items-center gap-2 text-sm font-bold font-display text-[#FF6B00] uppercase tracking-wider">
                  Learn more <ArrowRight size={14} weight="bold" className="group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
